import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  styled,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableCRUDLayout from '../CRUD/TableCRUDLayout';

import type { CircularProgressPropsColorOverrides } from '@mui/material/CircularProgress/CircularProgress';
import type { OverridableStringUnion } from '@mui/types';
import type { MatchCandidateResponse } from 'generated/index';

type Props = {
  candidates?: MatchCandidateResponse[];
  isLoading: boolean;
  onSearch: (searchValue: string) => void;
  onAssign: (candidate: MatchCandidateResponse) => void;
  onShowInactiveEmployments?: (showInactiveEmployments: boolean) => void;
  hideEmployeeType?: boolean;
  hideAppointments?: boolean;
  hideDuties?: boolean;
  hideQualifications?: boolean;
  hideContractMatch?: boolean;
  hideFunctionMatch?: boolean;
  hideResponse?: boolean;
  showInactiveEmployments?: boolean;
};

type PercentageIndicatorProps = {
  value: number;
  color: OverridableStringUnion<
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'inherit',
    CircularProgressPropsColorOverrides
  >;
};

type NumberIndicatorProps = {
  value: number;
};

function PercentageIndicator({ value, color }: PercentageIndicatorProps) {
  'use memo';

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={value * 100}
        color={color}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="caption" component="div">{`${Math.round(
          value * 100
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

function NumberIndicator({ value }: NumberIndicatorProps) {
  'use memo';

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        color={value > 0 ? 'error' : 'primary'}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="caption" component="div">{`${value}`}</Typography>
      </Box>
    </Box>
  );
}

const IconBox = styled(Box)`
  width: 100%;
  justify-content: center;
  display: flex;
  align-items: center;
`;

const responseTreshold = 0.5;

export default function AssignEmploymentGrid({
  candidates,
  isLoading,
  onAssign,
  onSearch,
  onShowInactiveEmployments,
  hideEmployeeType = true,
  hideAppointments = false,
  hideDuties = false,
  hideQualifications = false,
  hideContractMatch = false,
  hideFunctionMatch = false,
  hideResponse = false,
  showInactiveEmployments = false
}: Props) {
  'use memo';

  const { t } = useTranslation('common');
  const [searchValue, setSearchValue] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(9);

  function matches(text: string, partial: string) {
    return text.toLowerCase().indexOf(partial.toLowerCase()) > -1;
  }

  const columns = [
    ...[
      {
        field: 'employeeNumber',
        headerName: t('employeeNr'),
        flex: 1,
        maxWidth: 100
      },
      {
        field: 'employeeName',
        headerName: t('name'),
        flex: 1,
        minWidth: 170
      },
      {
        field: 'employmentNumber',
        headerName: t('employment'),
        flex: 1,
        maxWidth: 100
      },

      {
        field: 'functionName',
        headerName: t('function'),
        flex: 1,
        minWidth: 110
      }
    ],
    ...(hideEmployeeType
      ? []
      : [
          {
            field: 'employeeType',
            headerName: t('type'),
            minWidth: 130
          }
        ]),
    ...(hideAppointments
      ? []
      : [
          {
            field: 'overlapWithAppointmentsPercentage',
            headerName: t('appointment'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) => (
              <PercentageIndicator
                value={row.overlapWithAppointmentsPercentage}
                color="error"
              />
            )
          }
        ]),
    ...(hideDuties
      ? []
      : [
          {
            field: 'overlapWithDutiesPercentage',
            headerName: t('duty'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) => (
              <PercentageIndicator
                value={row.overlapWithDutiesPercentage}
                color="primary"
              />
            )
          }
        ]),
    ...(hideResponse
      ? []
      : [
          {
            field: 'responseMatchPercentage',
            headerName: t('reaction'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) =>
              row.responseMatchPercentage > responseTreshold ? (
                <IconBox>
                  <Icon type="check" color="primary" fontSize="small" />
                </IconBox>
              ) : (
                row.responseMatchPercentage < responseTreshold && (
                  <IconBox>
                    <Icon type="xmark" color="error" fontSize="small" />
                  </IconBox>
                )
              )
          }
        ]),
    ...(hideFunctionMatch
      ? []
      : [
          {
            field: 'functionMatchPercentage',
            headerName: t('function'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) =>
              row.functionMatchPercentage == 1 && (
                <IconBox>
                  <Icon type="check" color="primary" fontSize="small" />
                </IconBox>
              )
          }
        ]),
    ...(hideContractMatch
      ? []
      : [
          {
            field: 'contractMatchPercentage',
            headerName: t('contract'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) => (
              <IconBox>
                <PercentageIndicator
                  value={row.contractMatchPercentage}
                  color="primary"
                />
              </IconBox>
            )
          }
        ]),
    ...(hideQualifications
      ? []
      : [
          {
            field: 'missingQualifications',
            headerName: t('qualificationErrors'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) => (
              <IconBox>
                <Tooltip
                  title={
                    row.missingQualifications?.map((q) => q.name).join(', ') ||
                    ''
                  }
                >
                  <NumberIndicator
                    value={row.missingQualifications?.length ?? 0}
                  />
                </Tooltip>
              </IconBox>
            )
          }
        ]),
    ...(hideResponse
      ? []
      : [
          {
            field: 'responseRemark',
            headerName: t('remark'),
            flex: 1,
            maxWidth: 70,
            renderCell: ({ row }) =>
              row.responseRemark && (
                <IconBox>
                  <Tooltip title={row.responseRemark}>
                    <Icon
                      type="note"
                      color="error"
                      sx={{
                        fontSize: '15px',
                        alignItems: 'center'
                      }}
                    />
                  </Tooltip>
                </IconBox>
              )
          }
        ])
  ];

  const rows =
    candidates
      ?.filter(({ employeeName, employeeNumber }) => {
        return (
          matches(employeeName, searchValue) ||
          matches(employeeNumber.toString(), searchValue)
        );
      })
      .map((candidate) => ({
        id: `${candidate.employeeNumber}-${candidate.employmentNumber}`,
        employeeNumber: candidate.employeeNumber,
        employeeName: candidate.employeeName ?? '',
        employmentNumber: candidate.employmentNumber,
        functionName: candidate.function?.description ?? '',
        overlapWithAppointmentsPercentage:
          candidate.overlapWithAppointmentsPercentage,
        overlapWithDutiesPercentage: candidate.overlapWithDutiesPercentage,
        contractMatchPercentage: candidate.contractMatchPercentage,
        functionMatchPercentage: candidate.functionMatchPercentage,
        responseMatchPercentage: candidate.responseMatchPercentage,
        responseRemark: candidate.responseRemark,
        missingQualifications: candidate.missingQualifications,
        matchPercentage: candidate.matchPercentage,
        employeeType: candidate.isFlexEmployee
          ? t('flex')
          : t('deployableAsFlex')
      })) ?? [];

  const handleAssign = (id: string | number) => {
    const candidate = (candidates || []).find(
      ({ employeeNumber, employmentNumber }: MatchCandidateResponse) =>
        `${employeeNumber}-${employmentNumber}` === id
    );
    candidate && onAssign?.(candidate);
  };

  useEffect(() => {
    onShowInactiveEmployments?.(showInactiveEmployments);
  }, [onShowInactiveEmployments, showInactiveEmployments]);

  return (
    <>
      <Box sx={{ paddingTop: '20px', paddingBottom: '-25px' }}>
        <Typography variant={'h4'}>{t('searchEmployees')}</Typography>
      </Box>
      <Box sx={{ height: '650px' }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <TextField
            label={t('search')}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              onSearch(e.target.value);
            }}
          />

          <FormGroup>
            <FormControlLabel
              label={t('showInactiveEmployments')}
              checked={showInactiveEmployments}
              onChange={(_, isChecked: boolean) => {
                onShowInactiveEmployments?.(isChecked);
              }}
              control={<Checkbox />}
            />
          </FormGroup>
        </Box>

        <TableCRUDLayout
          containerHeight="auto"
          loading={isLoading}
          columns={columns}
          rows={!isLoading ? rows : []}
          editable={false}
          customActions={[
            {
              tooltipTitle: 'Koppelen',
              icon: 'link',
              onClick: handleAssign
            }
          ]}
          pagination
          pageSize={pageSize}
          pageSizeOptions={[10]}
          onPaginationModelChange={({ pageSize }) => setPageSize(pageSize)}
          translationKey={
            searchValue.length == 0
              ? t('noEmployeesFoundOnSelectedOrganizationalUnit')
              : t('noEmployeesFoundViaSearchString')
          }
        />
      </Box>
    </>
  );
}
